import clsx from 'clsx'
import { Field, useFormik } from 'formik'
import { FC, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import * as Yup from 'yup'
import * as StoreCRUD from '../StoreCRUD'
import { Toast } from '../../../modules/common/helper'
import Flatpickr from "react-flatpickr";
import moment from 'moment'
import {Vietnamese} from "flatpickr/dist/l10n/vn.js"
import { BUSINESS_TYPE_COMPANY, BUSINESS_TYPE_HOUSE_HOLD_1, BUSINESS_TYPE_HOUSE_HOLD_2, BUSINESS_TYPE_HOUSE_HOLD_3, BUSINESS_TYPE_HOUSE_HOLD_4, IMAGES_FORMATS, MAX_IMAGE_SIZE } from '../../../constants/common'
import { getUserByToken } from '../../../modules/auth/redux/AuthCRUD'
import { useDispatch } from 'react-redux'
import * as auth from '../../../modules/auth/redux/AuthRedux'
import { NumericFormat } from 'react-number-format'
import { revertPriceNumner } from '../../../selectors'
import { Address } from '../../../models/StoreModel'

interface IFormData {
  name: string
  business_name: string
  logo?: string
  logo_file?: File | null
  email: string
  phone: string
  status: string
  supplier_tax_code?: string
  template_code?: string
  invoice_series?: string
  invoice_type?: number
  viettel_username?: string
  viettel_password?: string
  expired_at?: string
  is_company?: number | string // 1: Doanh nghiệp, 0: Hộ kinh doanh
  cur_published_inv?: number,
  total_bought_invoice?: number,
  cur_total_inv?: number,
  invoice_min_num?: number,
  es_token_registed_at?: string,
  es_token_expired_at?: string,
  es_hsm_token_registed_at?: string,
  es_hsm_token_expired_at?: string,
  tax_sv_registed_at?: string,
  tax_sv_expired_at?: string,
  invoice_adjust_types?: string
}

const initialValues: IFormData = {
  name: '',
  is_company: BUSINESS_TYPE_HOUSE_HOLD_1,
  business_name: '',
  email: '',
  phone: '',
  status: '1',
  supplier_tax_code: '',
  template_code: '',
  invoice_series: '',
  invoice_type: 1,
  viettel_username: '',
  viettel_password: '',
  invoice_adjust_types: 'all'
}

interface IStoreInfoProps {
  modalVisible: boolean;
  toggleModal: (id?: number) => void;
  selectedId?: number;
  reloadData: boolean;
  handleReloadData: () => void;
  storeMana?: boolean
  showEditAddress?: (address: Address) => void
  showAddAddress?: () => void
  handleRemoveAddress?: (id: number) => void
}
const StoreInfo: FC<IStoreInfoProps> = ({ 
  modalVisible, toggleModal, selectedId, reloadData, 
  handleReloadData, storeMana, showEditAddress,
  showAddAddress,
  handleRemoveAddress
 }) => {
  const intl = useIntl()
  const accountSchema = Yup.object().shape({
    name: Yup.string().required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'Tên' })),
    business_name: Yup.string().required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'Tên đăng ký kinh doanh' })),
    email: Yup.string()
      .email(intl.formatMessage({ id: 'COMMON.EMAIL_NOT_VALID' }))
      .required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'email' })),
    phone: Yup.string()
      .max(255, intl.formatMessage({ id: 'COMMON.MAX_SYMBOLS' }, { max: 255 }))
      .required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'số điện thoại' })),
    supplier_tax_code: Yup.string().required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'Mã số thuế cửa hàng' }))
      .min(10, 'Mã số thuế không hợp lệ').max(14, 'Mã số thuế không hợp lệ'),
    viettel_username: Yup.string().required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'Viettel Username' })),
    viettel_password: Yup.string().required(intl.formatMessage({ id: 'COMMON.REQUIRED' }, { name: 'Viettel Password' })),
    logo_file: Yup.mixed().nullable().notRequired()
    .test("FILE_SIZE", "File tối đa 2MB.", 
        value => !value || (value && value.size <= MAX_IMAGE_SIZE))
    .test("FILE_FORMAT", "Vui lòng up ảnh.", 
        value => !value || (value && IMAGES_FORMATS.includes(value.type))),
  })

  const [logoImage, setLogoImage] = useState<string | ArrayBuffer | null>(null);
  const [invoiceUsage, setInvoiceUsage] = useState<string>('');
  const [addresses, setAddresses] = useState<Address[]>([])
  const [costCalcBy, setCostCalcBy] = useState<string>('time_lines')
  const [hasTrans, setHasTrans] = useState<number>(0)
  const formik = useFormik<IFormData>({
    initialValues,
    validationSchema: accountSchema,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      try {
        let res = null;
        var formData = new FormData();
        Object.entries(values).forEach(([key, value]: [string, any]) => {
          let aVal = (typeof value === 'object' && (key !== 'bank_qr' && key !== 'logo_file')) ? JSON.stringify(value)  : value;
          formData.append(key, aVal);
        });
        
        formData.append('cost_calc_by', costCalcBy)
        if (selectedId) {
          res = await StoreCRUD.update(formData, selectedId);
        } else {
          res = await StoreCRUD.create(formData);
        }

        Toast.fire({
          icon: 'success',
          title: intl.formatMessage({ id: 'COMMON.SUCCESS_NOTY' }),
        })
        toggleModal()
        handleReloadData()
        updateLocalUserData();
      } catch (error: any) {
        if (error?.response && error?.response?.status === 422) {
          const validationErrors = error?.response?.data?.errors;
          formik.setErrors(validationErrors);
          Toast.fire({
            icon: 'error',
            title: 'Đã có lỗi xảy ra vui lòng kiểm tra các trường nhập!',
          })
        }
      } finally {
        setSubmitting(false);
      }
    },
  })

  useEffect(() => {
    if (selectedId) {
      const fetchData = async () => {
        try {
          const { data } = await StoreCRUD.getDetail(selectedId);
          formik.setValues({
            name: data?.data?.name || '',
            is_company: data?.data?.is_company || BUSINESS_TYPE_HOUSE_HOLD_1,
            business_name: data?.data?.business_name || '',
            logo: data?.data?.logo || '',
            supplier_tax_code: data?.data?.supplier_tax_code || '',
            template_code: data?.data?.template_code || '',
            invoice_series: data?.data?.invoice_series || '',
            invoice_type: data?.data?.invoice_type || 1,
            viettel_username: data?.data?.viettel_username || '',
            viettel_password: data?.data?.viettel_password || '',
            email: data?.data?.email || '',
            phone: data?.data?.phone || '',
            status: data?.data?.status || '1',
            expired_at: data?.data?.expired_at,
            invoice_min_num: data?.data?.invoice_min_num || null,
            es_token_registed_at: data?.data?.es_token_registed_at || null,
            es_token_expired_at: data?.data?.es_token_expired_at || null,
            es_hsm_token_registed_at: data?.data?.es_hsm_token_registed_at || null,
            es_hsm_token_expired_at: data?.data?.es_hsm_token_expired_at || null,
            tax_sv_registed_at: data?.data?.tax_sv_registed_at || null,
            tax_sv_expired_at: data?.data?.tax_sv_expired_at || null,
            total_bought_invoice: data?.data?.total_bought_invoice || 0,
            invoice_adjust_types: data?.data?.invoice_adjust_types || 'all'
          });
          if (data?.data?.cur_published_inv && data?.data?.cur_total_inv) {
            setInvoiceUsage(`${data?.data?.cur_published_inv} / ${data?.data?.cur_total_inv}`);
          }
          setCostCalcBy(data?.data?.open_store_config?.cost_calc_by ?? 'time_lines');
          setAddresses(data?.data?.addresses ?? []);
          setLogoImage(data?.data?.logo || null);
          setHasTrans(data?.data?.has_transaction)
        } catch (error) {
          console.error('Error:', error);
        }
      };

      fetchData();
    }
  }, [selectedId]);

  const dispatch = useDispatch()
  const updateLocalUserData = async () => {
    const {data: user} = await getUserByToken()
    dispatch(auth.actions.fulfillUser(user));
  }

  const handleClose = () => {
    formik.resetForm();
    setLogoImage(null)
    toggleModal();
    document.querySelector('.store-modal')?.classList.add('modal-hidden');
  }
  const renderAddresses = (addresses: Address[]) => {
    return addresses?.map((a, index) => {
      return <div key={index} className='d-flex cursor-pointer'>
        <p className='mb-0' onClick={() => {
          if (storeMana) {
            showEditAddress?.(a)
            handleClose()
          }
        }}>
          ĐC{index+1}:
          {a.address ?? ''} {a.is_head_office == 1 ? '(Trụ sở chính)' : ''}
        </p>
        {a.is_default == 1 && (
          <i title='Mặc định' className='bi bi-check-circle text-success fs-3 ms-auto'></i>
        )}
        {storeMana && a.is_head_office != 1 && a.is_default == 0 && a.id && (
          <i title='Xóa' className='bi bi-trash text-danger fs-3 ms-auto'
            onClick={() => {
              if (!a.id) return console.warn('Address chưa có id');
              handleRemoveAddress?.(a.id);
              handleClose()
            }}
          ></i>
        )}
      </div>
    })
  }

  const checkHasError = () => {
    return !!(formik.touched && Object.keys(formik.touched).length && formik.errors && Object.keys(formik.errors).length)
  }
  return (
    <div key={selectedId} className={clsx('store-modal', { 'open': modalVisible })}>
      <div className='modal-content overflow-y-scroll'>
        <form className='form w-100' onSubmit={formik.handleSubmit}>
          <div className='row'>
            <div className='col-md-12'>
              <div className='card mb-5'>
                <div className='card-body row'>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='name'>
                      {intl.formatMessage({ id: 'STORE.NAME' })}
                    </label>
                    <input
                      {...formik.getFieldProps('name')}
                      id='name'
                      name='name'
                      value={formik.values.name || ''}
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.name && formik.errors.name },
                        {
                          'is-valid': formik.touched.name && !formik.errors.name,
                        }
                      )}
                      placeholder='Nhập họ tên'
                      autoComplete='off'
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.name}</span>
                      </div>
                    )}
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='logo'>
                      Logo
                    </label>
                    <input
                      {...formik.getFieldProps('logo_file')}
                      id='logo'
                      name='logo_file'
                      type='file'
                      value={undefined}
                      className={clsx(
                        'form-control form-control-lg'
                      )}
                      placeholder='Chọn logo'
                      autoComplete='off'
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        const file = event.currentTarget.files && event.currentTarget.files[0];
                        if (file) {
                          formik.setValues({
                            ...formik.values,
                            logo_file: file

                          });
                          const reader = new FileReader();
                          reader.onload = () => {
                            setLogoImage(reader.result);
                          };
                          reader.readAsDataURL(file);
                        }
                      }}
                    />
                    {formik.touched.logo_file && formik.errors.logo_file && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.logo_file}</span>
                      </div>
                    )}
                    {logoImage && (
                      <div className='mt-3'>
                        <img width={50} src={logoImage.toString()} alt="Preview" style={{ maxWidth: "100%" }} />
                      </div>
                    )}
                  </div>
                  {!storeMana && (
                    <>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='is_company'>
                          Loại hình kinh doanh
                        </label>
                        <select
                          {...formik.getFieldProps('is_company')}
                          id='is_company'
                          name='is_company'
                          className={clsx(
                            'form-select form-select-lg',
                            { 'is-invalid': formik.touched.is_company && formik.errors.is_company },
                            {
                              'is-valid': formik.touched.is_company && !formik.errors.is_company,
                            }
                          )}
                          autoComplete='off'
                        >
                          <option value={BUSINESS_TYPE_COMPANY}>Doanh nghiệp</option>
                          <option value={BUSINESS_TYPE_HOUSE_HOLD_1}>Hộ kinh doanh nhóm 1</option>
                          <option value={BUSINESS_TYPE_HOUSE_HOLD_2}>Hộ kinh doanh nhóm 2</option>
                          <option value={BUSINESS_TYPE_HOUSE_HOLD_3}>Hộ kinh doanh nhóm 3</option>
                          <option value={BUSINESS_TYPE_HOUSE_HOLD_4}>Hộ kinh doanh nhóm 4</option>
                        </select>
                        {formik.touched.is_company && formik.errors.is_company && (
                          <div className='fv-plugins-message-container text-danger'>
                            <span role='alert'>{formik.errors.is_company}</span>
                          </div>
                        )}
                      </div>
                      <div className='col-12 mb-3'>
                        <div className='p-2 bg-light-primary rounded'>
                          <label className='form-label required' htmlFor='invoice_adjust_types'>
                            Xử lý hóa đơn sai
                          </label>
                          <div className="d-flex justify-items-center justify-content-between">
                            <label className='d-flex cursor-pointer mb-2'>
                                <span className='form-check form-check-custom form-check-solid  me-3'>
                                    <input
                                      {...formik.getFieldProps('invoice_adjust_types')}
                                      className='form-check-input'
                                      type='radio'
                                      checked={formik.values.invoice_adjust_types === 'all'}
                                      name='invoice_adjust_types'
                                      value={'all'}
                                    />
                                </span>
                                <span className='d-flex align-items-center me-2'>Tất cả</span>
                            </label>
                            <label className='d-flex cursor-pointer mb-2'>
                                <span className='form-check form-check-custom form-check-solid  me-3'>
                                    <input
                                      {...formik.getFieldProps('invoice_adjust_types')}
                                      className='form-check-input'
                                      type='radio'
                                      checked={formik.values.invoice_adjust_types === 'adjust'}
                                      name='invoice_adjust_types'
                                      value={'adjust'}
                                    />
                                </span>
                                <span className='d-flex align-items-center me-2'>Điều chỉnh</span>
                            </label>
                            <label className='d-flex cursor-pointer mb-2'>
                                <span className='form-check form-check-custom form-check-solid  me-3'>
                                    <input
                                      {...formik.getFieldProps('invoice_adjust_types')}
                                      className='form-check-input'
                                      type='radio'
                                      checked={formik.values.invoice_adjust_types === 'replace'}
                                      name='invoice_adjust_types'
                                      value={'replace'}
                                    />
                                </span>
                                <span className='d-flex align-items-center me-2'>Thay thế</span>
                            </label>
                          </div>
                          <p className='mb-0 text-muted'>Chọn phương thức xử lý hóa đơn sai theo quy định áp dụng cho cửa hàng này.</p>
                        </div>
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='is_company'>
                          Cách tính giá vốn
                        </label>
                        <select
                          name='cost_calc_by'
                          className={'form-select form-select-lg'}
                          value={costCalcBy}
                          onChange={(e) => setCostCalcBy(e.target.value)}
                          disabled={hasTrans == 1}
                        >
                          <option value="time_lines">Theo dòng thời gian</option>
                          <option value="avg">Trung bình</option>
                        </select>
                        {formik.touched.status && formik.errors.status && (
                          <div className='fv-plugins-message-container text-danger'>
                            <span role='alert'>{formik.errors.status}</span>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='name'>
                      Tên đăng ký kinh doanh
                    </label>
                    <input
                      {...formik.getFieldProps('business_name')}
                      id='business_name'
                      name='business_name'
                      value={formik.values.business_name || ''}
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.business_name && formik.errors.business_name },
                        {
                          'is-valid': formik.touched.business_name && !formik.errors.business_name
                        }
                      )}
                      placeholder='Nhập tên đăng ký kinh doanh'
                      autoComplete='off'
                    />
                    {formik.touched.business_name && formik.errors.business_name && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.business_name}</span>
                      </div>
                    )}
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='email'>
                      {intl.formatMessage({ id: 'STORE.EMAIL' })}
                    </label>
                    <input
                      {...formik.getFieldProps('email')}
                      id='email'
                      name='email'
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.email && formik.errors.email },
                        {
                          'is-valid': formik.touched.email && !formik.errors.email,
                        }
                      )}
                      placeholder='Nhập địa chỉ email'
                      autoComplete='off'
                    />
                    {formik.touched.email && formik.errors.email && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.email}</span>
                      </div>
                    )}
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='phone'>
                      {intl.formatMessage({ id: 'STORE.PHONE' })}
                    </label>
                    <input
                      {...formik.getFieldProps('phone')}
                      id='phone'
                      name='phone'
                      type='text'
                      maxLength={255}
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.phone && formik.errors.phone },
                        {
                          'is-valid': formik.touched.phone && !formik.errors.phone,
                        }
                      )}
                      placeholder='Nhập  số điện thoại'
                      autoComplete='off'
                    />
                    {formik.touched.phone && formik.errors.phone && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.phone}</span>
                      </div>
                    )}
                  </div>
                  {storeMana && (
                    <div className='col-12 mb-3'>
                      <label className='form-label'>
                        {intl.formatMessage({ id: 'STORE.ADDRESS' })} 
                        <i 
                          className='text-primary bi bi-plus-circle ms-3 fs-3 cursor-pointer'
                          onClick={() => {
                            showAddAddress?.()
                            handleClose()
                          }}
                        ></i>
                      </label>
                      {renderAddresses(addresses)}
                    </div>
                  )}
                  
                  {!storeMana && (
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='status'>
                      {intl.formatMessage({ id: 'ACCOUNT.STATUS' })}
                    </label>
                    <select
                      {...formik.getFieldProps('status')}
                      id='status'
                      name='status'
                      className={clsx(
                        'form-select form-select-lg',
                        { 'is-invalid': formik.touched.status && formik.errors.status },
                        {
                          'is-valid': formik.touched.status && !formik.errors.status,
                        }
                      )}
                      autoComplete='off'
                    >
                      <option value='1'>{intl.formatMessage({ id: 'ACCOUNT.STATUS.ACTIVE' })}</option>
                      <option value='0'>{intl.formatMessage({ id: 'ACCOUNT.STATUS.INACTIVE' })}</option>
                    </select>
                    {formik.touched.status && formik.errors.status && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.status}</span>
                      </div>
                    )}
                  </div>
                  )}
                  {!storeMana && (
                    <div className='col-12 mb-3'>
                      <label className='form-label' htmlFor='status'>
                        Ngày hết hạn
                      </label>
                    <Flatpickr
                      name='expired_at'
                      key={formik.values.expired_at}
                      options={{
                          dateFormat: "d-m-Y",
                          locale: Vietnamese,
                          shorthandCurrentMonth: true,
                          defaultDate: moment(formik.values.expired_at).format('DD-MM-YYYY')
                      }}
                      onChange={(date) => {
                        formik.setValues({...formik.values, expired_at: moment(date[0]).format('YYYY-MM-DD')})
                      }}
                      className='form-control'
                      placeholder=''
                    />
                    </div>
                  )}
                  <strong className="my-5">Thông tin để xuất thông tin hoá đơn điện tử</strong>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='name'>
                      Mã số thuế cửa hàng
                    </label>
                    <input
                      {...formik.getFieldProps('supplier_tax_code')}
                      id='supplier_tax_code'
                      name='supplier_tax_code'
                      value={formik.values.supplier_tax_code || ''}
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.supplier_tax_code && formik.errors.supplier_tax_code },
                        {
                          'is-valid': formik.touched.supplier_tax_code && !formik.errors.supplier_tax_code
                        }
                      )}
                      placeholder='Nhập Mã số thuế cửa hàng'
                      autoComplete='off'
                    />
                    {formik.touched.supplier_tax_code && formik.errors.supplier_tax_code && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.supplier_tax_code}</span>
                      </div>
                    )}
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='name'>
                      Viettel Username
                    </label>
                    <input
                      {...formik.getFieldProps('viettel_username')}
                      id='viettel_username'
                      name='viettel_username'
                      value={formik.values.viettel_username || ''}
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.viettel_username && formik.errors.viettel_username },
                        {
                          'is-valid': formik.touched.viettel_username && !formik.errors.viettel_username
                        }
                      )}
                      placeholder='Nhập tên viettel username'
                      autoComplete='off'
                    />
                    {formik.touched.viettel_username && formik.errors.viettel_username && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.viettel_username}</span>
                      </div>
                    )}
                  </div>

                   <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='name'>
                      Viettel Pasword
                    </label>
                    <input
                      {...formik.getFieldProps('viettel_password')}
                      id='viettel_password'
                      name='viettel_password'
                      value={formik.values.viettel_password || ''}
                      type='password'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.viettel_password && formik.errors.viettel_password },
                        {
                          'is-valid': formik.touched.viettel_password && !formik.errors.viettel_password
                        }
                      )}
                      placeholder='Nhập tên viettel password'
                      autoComplete='off'
                    />
                    {formik.touched.viettel_password && formik.errors.viettel_password && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.viettel_password}</span>
                      </div>
                    )}
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='invoice_type'>
                      Loại hóa đơn
                    </label>
                    <select 
                      {...formik.getFieldProps('invoice_type')}
                      className='form-select'
                      value={formik.values.invoice_type}
                    >
                      <option value='1'>Hóa đơn giá trị gia tăng (01GTKT)</option>
                      <option value='2'>Hóa đơn bán hàng (02GTTT)</option>
                    </select>
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='template_code'>
                      Mẫu hóa đơn
                    </label>
                    <input
                      {...formik.getFieldProps('template_code')}
                      id='template_code'
                      name='template_code'
                      value={formik.values.template_code || ''}
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.template_code && formik.errors.template_code },
                        {
                          'is-valid': formik.touched.template_code && !formik.errors.template_code
                        }
                      )}
                      placeholder='Nhập Mẫu hóa đơn'
                      autoComplete='off'
                    />
                    {formik.touched.template_code && formik.errors.template_code && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.template_code}</span>
                      </div>
                    )}
                  </div>

                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='invoice_series'>
                      Ký hiệu hóa đơn
                    </label>
                    <input
                      {...formik.getFieldProps('invoice_series')}
                      id='invoice_series'
                      name='invoice_series'
                      value={formik.values.invoice_series || ''}
                      type='text'
                      className={clsx(
                        'form-control form-control-lg',
                        { 'is-invalid': formik.touched.invoice_series && formik.errors.invoice_series },
                        {
                          'is-valid': formik.touched.invoice_series && !formik.errors.invoice_series
                        }
                      )}
                      placeholder='Nhập Ký hiệu hóa đơn'
                      autoComplete='off'
                    />
                    {formik.touched.invoice_series && formik.errors.invoice_series && (
                      <div className='fv-plugins-message-container text-danger'>
                        <span role='alert'>{formik.errors.invoice_series}</span>
                      </div>
                    )}
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='invoice_min_num'>
                      Cảnh báo khi số lượng hóa đơn it hơn:
                    </label>
                    <input
                      {...formik.getFieldProps('invoice_min_num')}
                      id='invoice_min_num'
                      value={formik.values.invoice_min_num}
                      type='text'
                      className={'form-control form-control-lg'}
                      autoComplete='off'
                    />
                  </div>
                  <div className='col-12 mb-3'>
                    <label className='form-label' htmlFor='invoice_series'>
                      Tình trạng sử dụng hóa đơn
                    </label>
                    <input
                      {...formik.getFieldProps('invoice_series')}
                      id='invoice_series'
                      readOnly
                      disabled
                      value={invoiceUsage}
                      type='text'
                      className={'form-control form-control-lg'}
                      autoComplete='off'
                    />
                  </div>
                  {!storeMana && (
                    <>
                    <div className='col-12 mb-3'>
                      <label className='form-label' htmlFor='total_bought_invoice'>
                        Số hóa đơn đã mua
                      </label>
                      <NumericFormat
                        name='total_bought_invoice'
                        thousandSeparator=","
                        key={formik.values?.total_bought_invoice}
                        className={'form-control'}
                        defaultValue={formik.values?.total_bought_invoice ?? 0}
                        onBlur={(e) => {
                          formik.setValues({
                            ...formik.values,
                            total_bought_invoice: revertPriceNumner(e.target.value)
                          });
                        }}
                      />
                    </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='es_token_registed_at'>
                          Chữ ký số token: Ngày đăng ký 
                        </label>
                        <Flatpickr
                          name='es_token_registed_at'
                          options={{
                              dateFormat: "d-m-Y H:i:s",
                              enableTime: true,
                              locale: Vietnamese,
                              shorthandCurrentMonth: true
                          }}
                          value={formik.values.es_token_registed_at ? moment(formik.values.es_token_registed_at).format('DD-MM-YYYY HH:mm:ss') : ''} 
                          onChange={(date) => {
                            formik.setValues({...formik.values, es_token_registed_at: moment(date[0]).format('YYYY-MM-DD HH:mm:ss')})
                          }}
                          className='form-control'
                          placeholder=''
                        />
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='es_token_expired_at'>
                          Chữ ký số token: Ngày hết hạn 
                        </label>
                        <Flatpickr
                          name='es_token_expired_at'
                          options={{
                              dateFormat: "d-m-Y H:i:s",
                              enableTime: true,
                              locale: Vietnamese,
                              shorthandCurrentMonth: true
                          }}
                          value={formik.values.es_token_expired_at ? moment(formik.values.es_token_expired_at).format('DD-MM-YYYY HH:mm:ss') : ''} 
                          onChange={(date) => {
                            formik.setValues({...formik.values, es_token_expired_at: moment(date[0]).format('YYYY-MM-DD HH:mm:ss')})
                          }}
                          className='form-control'
                          placeholder=''
                        />
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='es_hsm_token_registed_at'>
                          Chữ ký số HSM: Ngày đăng ký 
                        </label>
                        <Flatpickr
                          name='es_hsm_token_registed_at'
                          options={{
                              dateFormat: "d-m-Y H:i:s",
                              enableTime: true,
                              locale: Vietnamese,
                              shorthandCurrentMonth: true
                          }}
                          value={formik.values.es_hsm_token_registed_at ? moment(formik.values.es_hsm_token_registed_at).format('DD-MM-YYYY HH:mm:ss') : ''} 
                          onChange={(date) => {
                            formik.setValues({...formik.values, es_hsm_token_registed_at: moment(date[0]).format('YYYY-MM-DD HH:mm:ss')})
                          }}
                          className='form-control'
                          placeholder=''
                        />
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='es_hsm_token_expired_at'>
                          Chữ ký số HSM: Ngày hết hạn 
                        </label>
                        <Flatpickr
                          name='es_hsm_token_expired_at'
                          options={{
                              dateFormat: "d-m-Y H:i:s",
                              enableTime: true,
                              locale: Vietnamese,
                              shorthandCurrentMonth: true
                          }}
                          value={formik.values.es_hsm_token_expired_at ? moment(formik.values.es_hsm_token_expired_at).format('DD-MM-YYYY HH:mm:ss') : ''} 
                          onChange={(date) => {
                            formik.setValues({...formik.values, es_hsm_token_expired_at: moment(date[0]).format('YYYY-MM-DD HH:mm:ss')})
                          }}
                          className='form-control'
                          placeholder=''
                        />
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='tax_sv_registed_at'>
                          Hỗ trợ báo cáo thuế: Ngày đăng ký 
                        </label>
                        <Flatpickr
                          name='tax_sv_registed_at'
                          options={{
                              dateFormat: "d-m-Y H:i:s",
                              enableTime: true,
                              locale: Vietnamese,
                              shorthandCurrentMonth: true
                          }}
                          value={formik.values.tax_sv_registed_at ? moment(formik.values.tax_sv_registed_at).format('DD-MM-YYYY HH:mm:ss') : ''} 
                          onChange={(date) => {
                            formik.setValues({...formik.values, tax_sv_registed_at: moment(date[0]).format('YYYY-MM-DD HH:mm:ss')})
                          }}
                          className='form-control'
                          placeholder=''
                        />
                      </div>
                      <div className='col-12 mb-3'>
                        <label className='form-label' htmlFor='tax_sv_expired_at'>
                          Hỗ trợ báo cáo thuế: Ngày hết hạn 
                        </label>
                        <Flatpickr
                          name='tax_sv_expired_at'
                          options={{
                              dateFormat: "d-m-Y H:i:s",
                              enableTime: true,
                              locale: Vietnamese,
                              shorthandCurrentMonth: true
                          }}
                          value={formik.values.tax_sv_expired_at ? moment(formik.values.tax_sv_expired_at).format('DD-MM-YYYY HH:mm:ss') : ''} 
                          onChange={(date) => {
                            formik.setValues({...formik.values, tax_sv_expired_at: moment(date[0]).format('YYYY-MM-DD HH:mm:ss')})
                          }}
                          className='form-control'
                          placeholder=''
                        />
                      </div>
                    </>
                  )} 
                  {checkHasError() && (
                    <div className='fv-plugins-message-container text-danger'>
                      <span role='alert'>Vui lòng kiểm tra dữ liệu nhập!</span>
                    </div>
                  )}
                  <div className='col-md-12 text-end'>
                    <button type='submit' className='btn btn-primary'>
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        fill='currentColor'
                        className='bi bi-floppy'
                        viewBox='0 0 16 16'
                      >
                        <path d='M11 2H9v3h2z' />
                        <path d='M1.5 0h11.586a1.5 1.5 0 0 1 1.06.44l1.415 1.414A1.5 1.5 0 0 1 16 2.914V14.5a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 14.5v-13A1.5 1.5 0 0 1 1.5 0M1 1.5v13a.5.5 0 0 0 .5.5H2v-4.5A1.5 1.5 0 0 1 3.5 9h9a1.5 1.5 0 0 1 1.5 1.5V15h.5a.5.5 0 0 0 .5-.5V2.914a.5.5 0 0 0-.146-.353l-1.415-1.415A.5.5 0 0 0 13.086 1H13v4.5A1.5 1.5 0 0 1 11.5 7h-7A1.5 1.5 0 0 1 3 5.5V1H1.5a.5.5 0 0 0-.5.5m3 4a.5.5 0 0 0 .5.5h7a.5.5 0 0 0 .5-.5V1H4zM3 15h10v-4.5a.5.5 0 0 0-.5-.5h-9a.5.5 0 0 0-.5.5z' />
                      </svg>{' '}
                      Lưu
                    </button>
                    <button
                      type='button'
                      className='btn btn-secondary ms-2 '
                      onClick={() => handleClose()}
                    >
                      <svg
                        xmlns='http://www.w3.org/2000/svg'
                        width='16'
                        height='16'
                        fill='currentColor'
                        className='bi bi-x'
                        viewBox='0 0 16 16'
                      >
                        <path
                          fillRule='evenodd'
                          d='M3.646 3.646a.5.5 0 0 1 .708 0L8 
                          7.293l3.646-3.647a.5.5 0 0 1 .708.708L8.707 
                          8l3.647 3.646a.5.5 0 0 1-.708.708L8 8.707l-
                          3.646 3.647a.5.5 0 0 1-.708-.708L7.293 
                          8 3.646 4.354a.5.5 0 0 1 0-.708z'
                        />
                      </svg>{' '}
                      Đóng
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}

export default StoreInfo
